import { lastValueFrom } from 'rxjs';
import { ClientListService } from '../services/client/client-list.service';

/**
 * Client typeahead search result
 */
export interface ClientTypeaheadResult {
  options: any[]; // Formatted client options for autocomplete
  addresses: Map<string, any>; // Map of client_id -> client data with addresses
  selectedClient?: any; // Currently selected client object (if any)
}

/**
 * Client typeahead utility for handling client search and data formatting
 */
export class ClientTypeaheadUtils {
  /**
   * Normalize client ID from various formats (object, string, number)
   */
  static normalizeClientId(value: any): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      if (value.value !== undefined && value.value !== null && value.value !== '') {
        return value.value;
      }
      if (value.id !== undefined && value.id !== null && value.id !== '') {
        return value.id;
      }
      if (value.client_id !== undefined && value.client_id !== null && value.client_id !== '') {
        return value.client_id;
      }
    }
    return value;
  }

  /**
   * Format client data into autocomplete option format
   */
  static formatClientOption(client: any): any {
    return {
      value: client.id ?? client.client_id ?? client.uuid ?? client._id,
      label: client.client_name ?? 
             client.name ?? 
             client.company_name ?? 
             client.code ?? 
             String(client.id ?? client.uuid ?? client._id ?? ''),
      client_code: client.client_code ?? client.code,
      ...client
    };
  }

  /**
   * Extract shipping and billing addresses from client data
   */
  static extractClientAddresses(client: any): { shipping: any[], billing: any[] } {
    const shippingAddresses: any[] = [];
    const billingAddresses: any[] = [];

    // Extract shipping address
    const shipping = client?.client_shipping_address;
    if (shipping?.id && shipping?.shipping_address) {
      shippingAddresses.push({
        value: shipping.id,
        label: shipping.shipping_address,
        ...shipping,
      });
    }

    // Extract billing addresses from tax_info
    if (Array.isArray(client.client_tax_info)) {
      client.client_tax_info.forEach((taxInfo: any) => {
        if (taxInfo?.id && taxInfo?.address) {
          billingAddresses.push({
            value: taxInfo.id,
            label: taxInfo.address,
            ...taxInfo,
          });
        }
      });
    }

    return { shipping: shippingAddresses, billing: billingAddresses };
  }

  /**
   * Search clients and return formatted result
   */
  static async searchClients(
    query: string,
    clientListService: ClientListService,
    currentClientId?: string | number | null,
    existingOptions: any[] = [],
    limit: number = 50
  ): Promise<ClientTypeaheadResult> {
    const normalizedQuery = (query || '').trim();
    
    // If query is empty, return empty result
    if (!normalizedQuery) {
      return {
        options: [],
        addresses: new Map(),
        selectedClient: existingOptions.find(opt => 
          currentClientId && String(opt.value) === String(currentClientId)
        )
      };
    }

    try {
      // Make API call with search query
      const response = await lastValueFrom(
        clientListService.getClients({
          type: 'non-draft',
          client_name: normalizedQuery,
          limit
        })
      );

      const clients = response?.data?.clients || response?.data || [];
      const clientOptions = clients.map((client: any) => this.formatClientOption(client));

      // Preserve currently selected client in options
      const existingClients = existingOptions.filter(opt => 
        currentClientId && String(opt.value) === String(currentClientId)
      );
      
      // Merge search results with existing clients
      const mergedOptions = [...existingClients, ...clientOptions];
      
      // Remove duplicates based on value
      const uniqueOptions = Array.from(
        new Map(mergedOptions.map(opt => [String(opt.value), opt])).values()
      );

      // Build address map
      const addressMap = new Map<string, any>();
      clientOptions.forEach((clientOption: any) => {
        const addresses = this.extractClientAddresses(clientOption);
        addressMap.set(String(clientOption.value ?? clientOption.id), {
          shipping: addresses.shipping,
          billing: addresses.billing,
          fullData: clientOption
        });
      });

      // Find selected client object
      const selectedClient = currentClientId 
        ? uniqueOptions.find(opt => String(opt.value) === String(currentClientId))
        : undefined;

      return {
        options: uniqueOptions,
        addresses: addressMap,
        selectedClient
      };
    } catch (error) {
      console.error('Error searching clients:', error);
      return {
        options: existingOptions,
        addresses: new Map(),
        selectedClient: existingOptions.find(opt => 
          currentClientId && String(opt.value) === String(currentClientId)
        )
      };
    }
  }

  /**
   * Load single client by ID and return formatted result
   */
  static async loadClientById(
    clientId: string | number,
    clientListService: ClientListService,
    includeSubsidiary: boolean = true
  ): Promise<ClientTypeaheadResult> {
    try {
      const clientResponse = await lastValueFrom(
        clientListService.getClientById(String(clientId), {
          include_subsidiary: includeSubsidiary ? 'true' : undefined
        })
      );
      
      const client = clientResponse?.data?.client || clientResponse?.data;
      if (!client) {
        return {
          options: [],
          addresses: new Map()
        };
      }

      const clientOption = this.formatClientOption(client);
      const addresses = this.extractClientAddresses(client);
      
      const addressMap = new Map<string, any>();
      addressMap.set(String(clientId), {
        shipping: addresses.shipping,
        billing: addresses.billing,
        fullData: client
      });

      return {
        options: [clientOption],
        addresses: addressMap,
        selectedClient: clientOption
      };
    } catch (error) {
      console.error('Error loading client:', error);
      return {
        options: [],
        addresses: new Map()
      };
    }
  }
}
